import { useState } from "react";
import useSWR from "swr";
import { BRANDS_CACHE_KEY } from "../constants/storageKeys";
import { ApiResponse } from "../typings/interfaces";
import { PublishedBrandModel } from "../typings/models";

const BRANDS_CACHE_TTL = 24 * 60 * 60 * 1000;
// Backend errors that can be caused by an outdated local brand list
export const OUTDATED_BRANDS_ERRORS = [
  "No brand available",
  "Search query is too short",
];

const readCachedBrands = <T>(): ApiResponse<T[]> | undefined => {
  try {
    const cached = JSON.parse(localStorage.getItem(BRANDS_CACHE_KEY) ?? "null");
    return Date.now() - cached?.savedAt < BRANDS_CACHE_TTL
      ? cached.response
      : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedBrands = <T>(response: ApiResponse<T[]>) => {
  if (!response?.data?.length) return;
  try {
    localStorage.setItem(
      BRANDS_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), response })
    );
  } catch {
    // The cache is optional, brands are fetched again on the next mount
  }
};

const useBrands = <T = PublishedBrandModel>(props = {}) => {
  const [cached] = useState(() => readCachedBrands<T>());
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    ApiResponse<T[]>
  >(`/brands/published`, {
    keepPreviousData: true,
    fallbackData: cached,
    revalidateOnMount: !cached,
    onSuccess: writeCachedBrands,
  });

  return {
    items: data?.data ?? [],
    isValidating,
    isLoading,
    error: (!data?.data && data?.error) || error || null,
    mutate,
  };
};

export default useBrands;
